"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/lib/i18n";
import { useResumeStore } from "@/lib/store/resume";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createId } from "@paralleldrive/cuid2";
import { produce } from "immer";
import { get } from "lodash";
import { Files, GripVertical, MoreHorizontal, Trash } from "lucide-react";
import React, { ElementType } from "react";
type TProps = {
  id: "workExperience" | "projects" | "skills" | "education";
  itemTitle: string;
  ItemContent: ElementType;
  data: any;
  sectionId: string;
  newItemIds: string[];
  isCustomSection?: Boolean;
};

const SortItem = (props: TProps) => {
  const { t } = useTranslation({ ns: "resume-sidebar" });

  const {
    id,
    itemTitle,
    ItemContent,
    data,
    sectionId,
    newItemIds,
    isCustomSection = false,
  } = props;
  const { setValue, resume } = useResumeStore();
  const section = get(resume.data.sections, sectionId);
  const items = section?.items || [];
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 100 : undefined,
    transition,
  };

  const onDuplicate = () => {
    if (!id) return;
    setValue(
      `sections.${sectionId}.items`,
      produce(items as unknown as string, (draft: any[]): void => {
        const content = draft.find((item) => item.id === id);
        draft.push({
          visible: true,
          ...content,
          id: createId(),
        });
      }),
    );
  };

  const onDelete = () => {
    if (!id) return;
    setValue(
      `sections.${sectionId}.items`,
      produce(items, (draft: any[]): void => {
        const index = draft.findIndex((item) => item.id === id);
        if (index === -1) return;
        draft.splice(index, 1);
      }),
    );
  };

  return (
    <div ref={setNodeRef} key={id} style={style} className="flex gap-1">
      <div
        {...listeners}
        {...attributes}
        className={"flex w-5 cursor-move items-center justify-center"}
      >
        <GripVertical size={18} />
      </div>

      <Accordion
        key={id}
        type="single"
        collapsible
        defaultValue={newItemIds.includes(id) ? `${sectionId}-${id}` : ""}
        className="w-full rounded-md border border-slate-200 bg-white px-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all hover:border-slate-300"
      >
        <AccordionItem
          value={`${sectionId}-${id}`}
          className="w-full border-0 "
          id={
            isCustomSection
              ? `builder-${sectionId.replace("custom.", "")}-${id}`
              : `builder-${sectionId}-${id}`
          }
        >
          <AccordionTrigger
            indicator={() => {
              return (
                <div className="flex items-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-6 w-6">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent color="primary">
                      <DropdownMenuItem key="duplicate" onClick={onDuplicate}>
                        <Files size={16} />
                        {t("left.duplicate")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        key="delete"
                        className="!text-danger"
                        color="danger"
                        onClick={onDelete}
                      >
                        <Trash size={16} />
                        {t("left.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            }}
            className="px-3 py-2 hover:no-underline"
          >
            <div className="flex w-full items-center justify-between gap-2 text-left">
              <span className="text-sm font-medium text-slate-700 transition-colors">
                {data[itemTitle]}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="border-t border-slate-100 px-3 py-2">
            <ItemContent {...data} sectionId={sectionId} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default SortItem;
